<template>
  <el-dialog :visible="show" @close="closeDialog">
    <template slot="title">
      <h5>{{ $t("DialogOrderDetail.Order_Information") }}｜<span class="text-muted">#{{ orderId }}</span></h5>
    </template>

    <div>

      <el-row>
        <!-- User info -->
        <el-col :span="24">
          <el-card shadow="never" v-loading="loading">
            <div>
              <h5>{{ user.name }}</h5>
              <p><i class="el-icon-message"></i>&nbsp;{{ user.email }}</p>
              <p><i class="el-icon-phone-outline"></i>&nbsp;{{ user.phone }}</p>
              <p></p>
            </div>
          </el-card>
          <br>
        </el-col>

        <!-- Order action buttons -->
        <el-col :span="24" class="d-flex justify-content-end">
          <el-button-group class="ml-1" v-if="userType === 'gaa'">
            <el-button type="warning" icon="el-icon-printer" @click="reprint(false)">{{ $t("DialogOrderDetail.Reprint_Order") }}</el-button>
            <el-button type="danger" icon="el-icon-printer" @click="reprint(true)">{{ $t("DialogOrderDetail.Urgent_Reprint") }}</el-button>
          </el-button-group>
          <el-button-group class="ml-1" v-if="userType === 'gaa'">
            <el-button type="warning" icon="el-icon-close" :disabled="order.meal_types !== 'delivery' || !!(order.is_lala_cancelled || order.is_cancelled)"
              @click="cancelLogistics">
              {{ $t("DialogOrderDetail.Cancel_Logistics") }}
            </el-button>
            <el-button type="warning" icon="el-icon-document-delete"
              @click="cancel(false)" :disabled="!!order.is_cancelled">
              {{ $t("DialogOrderDetail.Cancel_Order") }}
            </el-button>
            <el-button type="danger" icon="el-icon-document-delete"
              @click="cancel(true)" disabled>
              {{ $t("DialogOrderDetail.Force_Cancel") }}
            </el-button>
          </el-button-group>
          <el-button-group class="ml-1">
            <el-button icon="el-icon-refresh" @click="fetchOrderData">{{ $t("DialogOrderDetail.Refresh") }}</el-button>
            <el-button type="danger" icon="el-icon-remove-outline"  v-if="[ 'gaa', 'eaa' ].indexOf(userType) >= 0"
              @click="banUser">
              {{ $t("DialogOrderDetail.Block_User") }}
            </el-button>
          </el-button-group>
          <el-button-group v-if="userType==='gaa'" class="ml-1">
            <el-button type="danger" icon="el-icon-edit" @click="openPaymentChangeDialog">{{ $t("DialogOrderDetail.Payment_Changes") }}</el-button>
          </el-button-group>
        </el-col>
      </el-row>
      
      <el-divider></el-divider>

      <!-- Basic info data iterator -->
      <el-table :data="orderDataItems" :show-header="false">
        <el-table-column prop="key1" label="Key1">
          <template slot-scope="{row}"><strong>{{ row.key1 }}</strong></template>
        </el-table-column>
        <el-table-column prop="val1" label="Val1"></el-table-column>
        <el-table-column prop="key2" label="Key2">
          <template slot-scope="{row}"><strong>{{ row.key2 }}</strong></template>
        </el-table-column>
        <el-table-column prop="val2" label="Val2"></el-table-column>
      </el-table>

      <br>

      <!-- Logs of status -->
      <el-row v-loading="loading">
        <!-- Status -->
        <el-col :span="12">
          <h5>{{ $t("DialogOrderDetail.Order_Status") }}</h5>
          <el-timeline>
            <el-timeline-item v-for="log in statusLogs" :key="log.status">
              {{ toStatusLiteral(log.status) }} - {{ log.ts | toDateTimeString }}
            </el-timeline-item>
          </el-timeline>
        </el-col>
        <!-- Logistics Status -->
        <el-col :span="12">
          <h5>{{ $t("DialogOrderDetail.Logistics_Status") }}</h5>
          <el-timeline>
            <el-timeline-item v-for="log in lalamoveLogs" :key="log.status">
              {{ toStatusLiteral(log.status) }} - {{ log.ts | toDateTimeString }}
            </el-timeline-item>
          </el-timeline>
        </el-col>
      </el-row>
      <el-divider></el-divider>
      <!-- Logs of status -->
      <el-row v-if="order.payment_types !== 'cash'" v-loading="loading" >
         <!-- Payment Status -->
         <el-col>
          <h5>{{ $t("DialogOrderDetail.Online_Payment_Status") }}</h5>
          <el-table :data="paymentData" :show-header="false">
            <el-table-column prop="key" label="Key">
              <template slot-scope="{row}"><strong>{{ row.key }}</strong></template>
            </el-table-column>
            <el-table-column prop="val" label="Val">
              <template slot-scope="{row}">{{ row.val }}</template>
            </el-table-column>
          </el-table>
        </el-col>
      </el-row>
      <el-divider v-if="order.payment_types !== 'cash'"></el-divider>
      <!-- Cart items -->
      <h5>{{ $t("DialogOrderDetail.Ordered_Items") }}</h5>
      <el-table :data="cartItems" v-loading="loading">
        <el-table-column prop="name" :label="$t('DialogOrderDetail.Item')"></el-table-column>
        <el-table-column prop="amount" :label="$t('DialogOrderDetail.Unit_Price')" width="100">
          <template slot-scope="{row}">${{Number(row.amount).toLocaleString()}}</template>
        </el-table-column>
        <el-table-column prop="image" :label="$t('DialogOrderDetail.Image')" width="120">
          <template slot-scope="{row}">            
            <img :src="row.image || defaultProductImage" alt="...">
          </template>
        </el-table-column>
        <el-table-column prop="addon" :label="$t('DialogOrderDetail.Add_On')">
        </el-table-column>
        <el-table-column prop="qty" :label="$t('DialogOrderDetail.Quantity')" width="72">
        </el-table-column>
        <el-table-column prop="pointDeduction" :label="$t('DialogOrderDetail.pointDeduction')" width="100">
          <template slot-scope="{row}">
            -${{Number((row.pointDeduction)).toLocaleString()}}
          </template>
        </el-table-column>
        <el-table-column prop="ticketDiscount" :label="$t('DialogOrderDetail.productTicketDiscount')" width="100">
          <template slot-scope="{row}">
            -${{Number((row.ticketDiscount)).toLocaleString()}}
          </template>
        </el-table-column>
        <el-table-column :label="$t('DialogOrderDetail.Subtotal')" width="100">
          <template slot-scope="{row}">
            ${{Number((row.amount + row.addonAmount - row.pointDeduction - row.ticketDiscount) * row.qty).toLocaleString()}}
          </template>
        </el-table-column>
      </el-table>
      <el-divider></el-divider>

      <!-- Order addons -->
      <h5>{{ $t('DialogOrderDetail.Bulk_Add_On') }}</h5>
      <el-card shadow="never">
        <div class="d-flex justify-content-between" v-if="order.addon">
          <span><b>{{ order.addon.display }}</b></span>
          <span>${{ Number(order.addon.amount).toLocaleString() }}</span>
        </div>
      </el-card>


    </div>
    <!-- Dialog of payment change -->
    <PaymentChangeDialog :show.sync="dialog.paymentChange.show" :order-id="orderId"  @close="dialog.paymentChange.show = false">
    </PaymentChangeDialog>

  </el-dialog>
</template>

<script>

import axios from 'axios';
import moment from 'moment';
import PaymentChangeDialog from './DialogPaymentChange.vue'; 
import { getInvoiceSetting } from "src/javascripts/gaa/fetchData";

export default {
  name: 'OrderDetailDialog',
  props: {
    show: Boolean,
    orderId: String
  },
  components: {
    PaymentChangeDialog
  },
  mounted() {
    if (this.orderId)
      this.fetchOrderData();
  },

  methods: {
    closeDialog() {
      this.$emit('update:show', false);
    },

    fetchOrderData() {
      this.loading = true;
      axios.get(
        `${process.env.VUE_APP_APIHOST}/${this.userType}/orders/${this.orderId}/cart-snapshot`
        + `?reveal=${this.userType === 'gaa'? 'true': 'false'}`
      )
      .then(response => {
        this.order = response.data.order;
        this.user = response.data.user;
        this.cart = response.data.cart;
        this.statusLogs = response.data.statusLogs;
        this.lalamoveLogs = response.data.lalamoveLogs;
      })
      .catch(err => {
        console.error(`[DialogOrderDetail] fetchOrderData: failed to fetch snapshot`, err);

        //TODO: Make fallback request.
        return axios.get(
          `${process.env.VUE_APP_APIHOST}/${this.userType}/orders/${this.orderId}/cart`
          + `?reveal=${this.userType === 'gaa'? 'true': 'false'}`
        );
      })
      .then(response => {
        if (!response) return;//NOTE: Cart data has been retrieved.

        console.warn(`[DialogOrderDetail] fetchOrderData: tried fallback cart request.`, response.data);
        this.order = response.data.order;
        this.user = response.data.user;
        this.cart = response.data.cart;
        this.statusLogs = response.data.statusLogs;
        this.lalamoveLogs = response.data.lalamoveLogs;
      })
      .catch(err => {
        console.error(`[DialogOrderDetail] fetchOrderData: fallback request failed as well`, err);
        let message;
        if (err.data && err.data.message)
          message = err.data.message;
        this.$notify.error({
          title: this.$t("DialogOrderDetail.Message.Unable_to_get_order_detail"),
          message: message || ''
        });
      })
      .finally(() => {
        this.loading = false;
      });
    },

    // Order action methods.
    reprint(_isEmergent = false) {
      this.$confirm(
        this.$t("DialogOrderDetail.Message.Confirm_to_reprint_order", {shop_name: this.order['shop_name']}),
        this.$t("DialogOrderDetail.Message.Reprint_order", {_isEmergent: _isEmergent ? this.$t("DialogOrderDetail.Emergency") : ""})
        , { type: 'warning' }
      )
      .then(() => {
        axios.post(
          `${process.env.VUE_APP_APIHOST}/${this.userType}/qcpos/orders/${this.orderId}`
          + `/${_isEmergent === true? 'emergent-': ''}reprint`
        )
        .then(response => {
          this.$message({ type: 'success', message: this.$t("DialogOrderDetail.Message.Reprint_request_has_been_sent") });
        })
        .catch(err => {
          console.error(`Cannot request reprint. (Emergency? ${_isEmergent})`, err.response);
          this.$message.error(this.$t("DialogOrderDetail.Message.Reprint_request_failed"));
        });
      });
    },
    cancel(_isForced = false) {
      if(this.isInvoiceEnable){
        this.$confirm(
          this.$t("DialogOrderDetail.Message.due_to_invoice_service_please_cancel_order_at_PDGL"),
          { type: 'warning', showCancelButton: false }
        )
        return;
      }

      this.$confirm(
        this.$t("DialogOrderDetail.Message.Confirm_to_cancel_order", {orderId: this.orderId}),
        this.$t("DialogOrderDetail.Message.Force_to_cancel_order", {_isForced: _isForced ? this.$t("DialogOrderDetail.Force") : ""}),
        { type: 'warning' }
      )
      .then(() => {
        axios.post(
          `${process.env.VUE_APP_APIHOST}/${this.userType}/qcpos/orders/${this.orderId}`
          + `/${_isForced? 'force-': ''}cancel`
        )
        .then(response => {
          this.$message({ type: 'success', message: this.$t("DialogOrderDetail.Message.Cancel_order_request_has_been_issued")});
        })
        .catch(err => {
          console.error(`Cannot cancel somehow.`, err.response);
          this.$message.error(this.$t("DialogOrderDetail.Message.Unable_to_canel_order_or_refund"));
        });
      });
    },
    cancelLogistics() {
      if (!this.order['lalamove_ref']) {
        this.$message({ type: 'warning', message: this.$t("DialogOrderDetail.Message.Do_not_need_to_cancel_because_of_self_delivery") });
        return;
      }
      this.$confirm(
        this.$t("DialogOrderDetail.Message.Unable_to_cancel_logisitcs_when_delivering_delivered"),
        this.$t("DialogOrderDetail.Message.Confirm_to_cancel_logistics", {orderId: this.orderId}),
        { type: 'warning' }
      )
      .then(() => {

        axios.delete(
          `${process.env.VUE_APP_APIHOST}/${this.userType}/qcpos/orders/cancel-lala/${this.orderId}`
        )
        .then(response => {
          this.$message({ type: 'success', message: this.$t("DialogOrderDetail.Message.Cancel_logistics_request_has_been_issued") });
        })
        .catch(err => {
          console.error(`Cannot cancel the delivery somehow.`, err.response);
          this.$message.error(this.$t("DialogOrderDetail.Message.Unable_to_cancel_logistics"));
        });

      });

    },
    banUser() {
      this.$confirm(
        this.$t("DialogOrderDetail.Message.Confirm_to_add_member_to_black_list", { name: this.user['name'], shop_name: this.order['shop_name']}),
        this.$t("DialogOrderDetail.Message.Block_member"),
        { type: 'warning' }
      )
      .then(() => {
        axios.post(
          `${process.env.VUE_APP_APIHOST}/${this.userType}/shops/${this.order['account_id']}/ban-user`,
          { "orderId": this.orderId, "userId": this.user['id'] }
        )
        .then(response => {
          this.$message({
            type: 'success', message: this.$t("DialogOrderDetail.Message.Add_member_to_black_list", { shop_name: this.order['shop_name'], name: this.user['name'] }),
          });
        })
        .catch(err => {
          console.error(`Cannot block this user somehow.`, err);
          this.$message.error(this.$t("DialogOrderDetail.Message.Unable_to_block_member"));
        });
      });
    },
    openPaymentChangeDialog () {
      this.dialog.paymentChange.show = true;
    },

    fetchPaymentData() {
      this.loading = true;
      // let url = `http://localhost:3000/orders/${this.order.transaction_id}/info`;
      let url = `${process.env.VUE_APP_APIHOST}/${this.userType}/ddpw/orders/${this.order.transaction_id}`;
      const self = this;
      // this.payment = [];
      axios.get(url)
      .then(response => {
        this.payment = response.data;
        // this.paymentData.push(
        //   { key: '狀態', val: payment.status },
        //   { key: '線上支付交易碼', val: payment.orderId },
        //   { key: 'DDPG訂單編號', val: payment.paymentOrderId },
        //   { key: '交易時間', val: self.$options.filters.toDateTimeString(payment.tradeTs) }
        // )
      })
      .catch(err => {
        console.error(`[DialogOrderDetail] fetchPaymentData: failed to fetch payment data`, err);
        // this.paymentData = [];
        this.payment = null;
      })
      .finally(() => {
        this.loading = false;
      });
    },

    toStatusLiteral(status) {
      const statusObj = {
        'new': this.$t("DialogOrderDetail.New_Order"),
        'accepted': this.$t("DialogOrderDetail.Order_Accepted"),
        'rejected': `${this.$t("DialogOrderDetail.Canceled")}（${this.$t("DialogOrderDetail.Rejected")}）`,
        'done': this.$t("DialogOrderDetail.Completed"),
        'cancelled': this.$t("DialogOrderDetail.Canceled"),
        'lala_assigning_driver': this.$t("DialogOrderDetail.In_Logistics_Matchmaking"),
        'lala_on_going': this.$t("DialogOrderDetail.Logistics_En_Route_For_Pickup"),
        'lala_picked_up': this.$t("DialogOrderDetail.Logistics_Picked_Up"),
        'lala_completed': this.$t("DialogOrderDetail.Logistics_Delivered"),
        'lala_rejected': `${this.$t("DialogOrderDetail.Logistics_Canceled")}（${this.$t("DialogOrderDetail.Rejected")}）`,
        'lala_cancelled': this.$t("DialogOrderDetail.Logistics_Canceled"),
        'lala_expired': this.$t("DialogOrderDetail.Logistics_Overdue"),
        'customer_service': this.$t("DialogOrderDetail.Transferred_To_Customer_Service")
      }
      return statusObj[status]
    }
  },

  data() {
    return {
      loading: false,
      order: { transaction_id: null },
      user: { },
      cart: [ ],
      statusLogs: [ ],
      lalamoveLogs: [ ],

      // Misc
      defaultProductImage: `${process.env.BASE_URL}img/dish.png`,
      dialog: {
        paymentChange: {
          show: false,
          // orderId: null
        }
      },
      payment: null,
      // paymentData: [],
    }
  },

  computed: {
    PAY_TYPES() {
      return {
          'cash': this.$t("DialogOrderDetail.Pay_On_Site"),
          'multiple': this.$t("DialogOrderDetail.Online_Payment"),
          'qrpay_apple': 'Apple Pay',
          'qrpay_google': 'Google Pay',
          'qrpay_line': 'LINE Pay',
          'qrpay_credit': this.$t("DialogOrderDetail.Credit_Card"),
          'qrpay_easywallet': this.$t("DialogOrderDetail.Easy_Wallet"),
          'qrpay_jko': this.$t("DialogOrderDetail.JKO_Pay"),
          'qrpay_icashpay': 'icash Pay',
          'qrpay_taiwanpay': this.$t("DialogOrderDetail.Taiwan_Pay"),
          'taiwanpay': this.$t("DialogOrderDetail.Taiwan_Pay"),
          'qrpay_pxpayplus': '全支付',
          'pxpayplus': '全支付',
          'qrpay_pluspay': '全盈支付',
          'pluspay': '全盈支付',
          'qrpay_gcash_ewallet': 'GCash Web',
          'qrpay_gcash_scan2pay': 'GCash Scan-to-pay',
          'qrpay_maya_web': 'Maya Web',
          'qrpay_maya_scan2pay': 'Maya Scan-to-pay',
          'twqr': 'TWQR',
          'yuanta': 'TWQR',
          'qrpay_twqr': 'TWQR',
          'qrpay_yuanta': 'TWQR',
          'qrpay_aftee': 'Aftee',
          'aftee': 'Aftee'
        }
    },
    orderDataItems() {
      // List enum-term mappings.
      // let PAY_TYPES = {
      //   'cash': '現場付款', 'multiple': '線上支付',
      //   'qrpay_apple': 'Apple Pay', 'qrpay_google': 'Google Pay',
      //   'qrpay_line': 'LINE Pay', 'qrpay_credit': '一般信用卡',
      //   'qrpay_easywallet': '悠遊付',
      //   'qrpay_jko': '街口支付',
      //   'qrpay_taiwanpay': '台灣Pay'
      // };
      let MEAL_TYPES = this.$route.meta.retail?
        { 'eat_in': this.$t("DialogOrderDetail.Self_Pickup"), 'to_go': this.$t("DialogOrderDetail.Customization"), 'delivery': this.$t("DialogOrderDetail.Home_Delivery"), 'takeaway': this.$t("DialogOrderDetail.Customization"), }:
        { 'eat_in': this.$t("DialogOrderDetail.Dine_In"), 'to_go': this.$t("DialogOrderDetail.Self_Pickup"), 'delivery': this.$t("DialogOrderDetail.Delivery"), 'takeaway': this.$t("DialogOrderDetail.Takeaway"), };
      let PLATFORMS = {
        // 'quickclick': '快一點',
        'quickclick': process.env.VUE_APP_BRANDNAME,
        'gofoodie': 'GoFoodie',
        'ubereats': 'Uber Eats',
        'foodpanda': 'Foodpanda',
        'counter': this.$t("DialogOrderDetail.Counter_Ordering")
      };

      // List data items to be displayed.
      let items = [
        { key: this.$t("DialogOrderDetail.Store"), val: this.order['shop_name'] },
        { key: this.$t("DialogOrderDetail.Order_Number_ID"), val: this.order['order_id'] },
        { key: this.$t("DialogOrderDetail.Serial_Number_Pickup_Number"), val: this.order['order_number'] },
        { key: this.$t("DialogOrderDetail.Order_Platform"), val: PLATFORMS[ this.order['platform'] ] },

        { key: this.$t("DialogOrderDetail.Order_Time"), val: moment.unix(this.order['created_at']).format('YYYY/M/D h:mmA') },
        { key: this.$t("DialogOrderDetail.Pickup_Delivery_Time"), val: moment.unix(this.order['take_meal_time']).format('YYYY/M/D h:mmA') },
        
        { key: this.$t("DialogOrderDetail.Pickup_Delivery_Method"), val: MEAL_TYPES[ this.order['meal_types'] ] },
        { key: this.$t("DialogOrderDetail.Payment_Method"), val: this.PAY_TYPES[ this.order['payment_types'] ] },
        { key: this.$t("DialogOrderDetail.Online_Payment_Transaction_Code"), val: this.order['transaction_id'] },

        { key: this.$t("DialogOrderDetail.Product_Amount"), val: `$${Number(this.order['cart']).toLocaleString()}` },
        { key: this.$t("DialogOrderDetail.Store_Discount"), val: `-$${Number(this.order['discount']).toLocaleString()}` },
        { key: this.$t("DialogOrderDetail.Quick_Cash_Deduction"), val: `-$${Number((this.order['qc_money'] || 0)).toLocaleString()}` },
        { key: this.$t("DialogOrderDetail.Points_Discount"), val: `-$${Number(this.order['pointDiscount']).toLocaleString()}` },

        { key: this.$t("DialogOrderDetail.Shipping_Fee"), val: `$${Number(this.order['delivery_fee']).toLocaleString()}` },
        { key: this.$t("DialogOrderDetail.Store_Pays_Shipping_Fee"), val: `$${Number(this.order['shop_covered_fee']).toLocaleString()}` },
        { key: this.$t("DialogOrderDetail.Total_Paid"), val: `$${Number(this.order['amount']).toLocaleString()}` },
        { key: `${this.$t("DialogOrderDetail.Order_Placed")}?`, val: this.order['is_confirmed']? this.$t("DialogOrderDetail.Yes"): this.$t("DialogOrderDetail.No") },
        { key: `${this.$t("DialogOrderDetail.Order_Canceled")}?`, val: this.order['is_cancelled']? this.$t("DialogOrderDetail.Yes"): this.$t("DialogOrderDetail.No") },

        { key: `${this.$t("DialogOrderDetail.Table_Number")} (${this.$t("DialogOrderDetail.Dine_In")})`, val: this.order['table_number'] || '' },
        { key: this.$t("DialogOrderDetail.Remark"), val: this.order['notes'] }
      ];

      // If it's a delivery order, include delivery info.
      if (this.order['meal_types'] === 'delivery') {
        // Prioritize delivery_address, fallback to content.options.deliveryAddress if empty
        let deliveryAddress = this.order['delivery_address'];
        if (!deliveryAddress && this.order['content']) {
          try {
            const content = typeof this.order['content'] === 'string' ? JSON.parse(this.order['content']) : this.order['content'];
            if (content.options && content.options.deliveryAddress) {
              deliveryAddress = content.options.deliveryAddress;
            }
          } catch (e) {
            console.error('Failed to parse order content:', e);
          }
        }

        items.push(
          { key: `${this.$t("DialogOrderDetail.Agent_Logistics_Canceled")}?`, val: this.order['is_lala_cancelled']? this.$t("DialogOrderDetail.Yes"): this.$t("DialogOrderDetail.No") },
          { key: this.$t("DialogOrderDetail.Logistics_Pickup_Time"), val: this.order['lala_take_meal_time']? moment.unix(this.order['lala_take_meal_time']).format('YYYY/M/D h:mmA'): this.$t("DialogOrderDetail.Message.No_time_specified") },
          { key: this.$t("DialogOrderDetail.Delivery_Address"), val: deliveryAddress },
          { key: this.$t("DialogOrderDetail.Message.Delivery_time"), val: this.order['delivery_time'] },
          { key: this.$t("DialogOrderDetail.Delivery_Remark"), val: this.order['lala_notes'] }
        );
      }

      // Convert to table column structures for this data iterator.
      let ret = [ ];
      for (let i=0; i<items.length; i+=2) {
        let temp = {
          'key1': items[i].key,
          'val1': items[i].val,
        };
        if (i + 1 < items.length) {
          temp['key2'] = items[i + 1].key;
          temp['val2'] = items[i + 1].val;
        }
        ret.push(temp);
      }
      return ret;
    },

    cartItems() {
      return this.cart;
    },

    userType() {
      return this.$store.getters.userType;
    },

    paymentData() {
      let paymentData = [];
      if (this.payment) {
        paymentData.push(
          { key: this.$t("DialogOrderDetail.Online_Payment_Transaction_Code"), val: this.payment.orderId },
          { key: this.$t("DialogOrderDetail.DDPG_Order_Number"), val: this.payment.paymentOrderId },
          { key: this.$t("DialogOrderDetail.Status"), val: this.payment.status },
          { key: this.$t("DialogOrderDetail.Transaction_Time"), val: this.$options.filters.toDateTimeString(this.payment.tradeTs) }
        );
        if (this.payment.points) {
          paymentData.push({ key: this.$t("DialogOrderDetail.Points_Discount"), val: `${this.payment.pointName}：${this.payment.points} (\$${this.payment.pointAmount})` })
        }
        if (this.payment.ticketId) {
          paymentData.push({ key: this.$t("DialogOrderDetail.Coupon_Discount"), val: `${this.payment.ticketTitle}：\$${this.payment.amount - this.payment.ticketQuotation} ` })
        }
        if (this.payment.paymentAmount) {
          paymentData.push({ key: this.$t("DialogOrderDetail.Other_Payment_Methods"), val: `${this.PAY_TYPES[ this.order['payment_types'] ]}：\$${this.payment.paymentAmount} ` })
        }
      } else {
        paymentData.push(
          { key: this.$t("DialogOrderDetail.Online_Payment_Transaction_Code"), val: this.order.transaction_id },
          { key: this.$t("DialogOrderDetail.DDPG_Order_Number"), val: this.order.transaction_id },
          { key: this.$t("DialogOrderDetail.Status"), val: '' },
          { key: this.$t("DialogOrderDetail.Transaction_Time"), val: '' }
        );
      }
      
      return paymentData;
    }
  },

  filters: {
    toDateTimeString(ts) {
      return moment.unix(ts).format('YYYY/M/D h:mmA');
    },
  },

  watch: {
    orderId: function(id) {
      this.fetchOrderData();
    },
    'order.transaction_id': function(id) {
      if (id)
        this.fetchPaymentData();
      else
        this.payment = null;
    },
    order: function(order) {
      const accountId = order['account_id']
      if(accountId){
        getInvoiceSetting(accountId)
        .then(res => {
          this.isInvoiceEnable = res.data.isInvoiceEnable
        })
        .catch(err => {
          console.log("getInvoiceSetting err: ", err);
        });
      }
    },
  }
}
</script>

<style scoped>

</style>